import { useState } from 'react';
import { GitBranch, ChevronDown, RefreshCw } from 'lucide-react';
import { Commit } from '../../types';
import { CommitItem } from './CommitItem';
import { Spinner } from '../Common';
import clsx from 'clsx';

interface CommitHistoryProps {
  commits: Commit[];
  branches: string[];
  currentBranch: string;
  selectedCommit?: Commit | null;
  loading?: boolean;
  onSelectCommit: (commit: Commit) => void;
  onBranchChange: (branch: string) => void;
  onRefresh: () => void;
}

export function CommitHistory({
  commits,
  branches,
  currentBranch,
  selectedCommit,
  loading,
  onSelectCommit,
  onBranchChange,
  onRefresh,
}: CommitHistoryProps) {
  const [showBranchMenu, setShowBranchMenu] = useState(false);

  return (
    <div className="flex flex-col h-full bg-white border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-gray-900">Version History</h3>
        <div className="flex items-center gap-2">
          {/* Branch selector */}
          <div className="relative">
            <button
              onClick={() => setShowBranchMenu(!showBranchMenu)}
              className="flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
            >
              <GitBranch className="w-4 h-4" />
              {currentBranch}
              <ChevronDown className="w-4 h-4" />
            </button>

            {showBranchMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white border rounded-lg shadow-lg py-1 z-10">
                {branches.map((branch) => (
                  <button
                    key={branch}
                    onClick={() => {
                      onBranchChange(branch);
                      setShowBranchMenu(false);
                    }}
                    className={clsx(
                      'w-full px-4 py-2 text-left text-sm hover:bg-gray-50',
                      branch === currentBranch && 'bg-primary-50 text-primary-700'
                    )}
                  >
                    {branch}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Spinner />
          </div>
        ) : commits.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No commits found
          </div>
        ) : (
          <div className="divide-y">
            {commits.map((commit) => (
              <CommitItem
                key={commit.sha}
                commit={commit}
                selected={selectedCommit?.sha === commit.sha}
                onClick={() => onSelectCommit(commit)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
